'use client';

import { deleteBit, likeBit } from '@/src/actions/bits';
import { CoverImage } from './components/CoverImage';
import { IBit, IBitComment } from 'types/src/types';
import { TagsList } from './components/TagsList';
import { ContentRenderer } from './components/ContentRenderer';
import { AuthorInfo } from './components/AuthorInfo';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog';
import { useBoolean } from '@/src/hooks/use-boolean';
import { redirect, useRouter } from 'next/navigation';
import { Icon, Spinner, useToast } from '@/src/components/ui';
import { ShareLink } from './components/ShareLink';
import { Report } from './components/Report';
import { useProfile } from '@/src/hooks/use-profile';
import { CommentsSection } from './components/CommentsSection';
import { useLayoutEffect, useState } from 'react';
import { getBitComments } from '@/src/actions/comments';
import { cn } from '@workspace/ui/lib/utils';
import { Donate } from '../profile/components/buttons/Donate';
import PageLoader from '@/src/components/ui/PageLoader';
import { LinkProject } from './components/LinkProject';
import { BitsList } from '../exploreStories/components/BitsList';

type BitProps = {
  bit: IBit | null;
  comments: IBitComment[] | [];
  readAlso?: IBit[] | null;
};

export default function Bit({
  bit = null,
  comments: initialComments = [],
  readAlso = null,
}: BitProps) {
  const [comments, setComments] = useState<IBitComment[]>(initialComments);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [like, setLike] = useState({
    isLiked: bit?.likes.isMyLike || false,
    count: bit?.likes.count || 0,
  });
  const { data: profile } = useProfile();

  useLayoutEffect(() => {
    setIsAnonymous(profile?.type === 'anonymous');
  }, [profile?.type]);

  const refreshComments = async () => {
    if (!bit?.id) return;
    const res = await getBitComments(bit?.id);
    if (res.data) {
      setComments(res.data);
    } else {
      console.error('Failed to refresh comments', res.error);
    }
  };
  const isLoading = useBoolean(false);
  const router = useRouter();
  const toast = useToast();

  const isMyBit = profile?.id === bit?.author.id;

  const handleDeleteBit = async () => {
    if (!bit) return;
    isLoading.onTrue();

    try {
      const { data, error } = await deleteBit(bit.id);
      if (!data && error) {
        console.error('Error deleting bit:', error);
        toast.error('Failed to delete bit');
        return;
      }
      router.push(`/${bit.author.uniqueLink}`);
      toast.success('Bit deleted successfully');
    } catch (error) {
      console.error('Error deleting bit:', error);
    } finally {
      isLoading.onFalse();
    }
  };

  const handleLikeBit = async () => {
    try {
      if (!bit?.id) return;
      setLike((prev) => ({
        isLiked: !prev.isLiked,
        count: +prev.count + (prev.isLiked ? -1 : 1),
      }));
      await likeBit(bit.id);
    } catch (error) {
      console.error('Error liking bit:', error);
      setLike((prev) => ({ isLiked: !prev.isLiked, count: +prev.count }));
    }
  };

  return (
    <>
      {bit ? (
        <div className="w-full mb-10">
          <div className="flex lg:justify-between flex-col lg:flex-row">
            <div className="lg:w-[745px] w-full">
              <CoverImage url={bit?.coverImageUrl as string} />
              <section className="px-4 md:px-9 mt-7 md:mt-10 w-full">
                <div className="flex w-full justify-between items-center">
                  <h1 className="md:text-[40px]! text-2xl! font-semibold">
                    {bit?.title}
                  </h1>
                </div>
                <TagsList tags={bit?.tags || []} />
                <div className="flex flex-col md:gap-4 gap-2 w-full">
                  {bit?.fields.map((field) => (
                    <ContentRenderer key={field.id} field={field} />
                  ))}
                </div>
              </section>
            </div>
            <hr className="md:hidden border-t border-gray-200 mt-7 w-full" />
            <div className="flex flex-col h-fit lg:w-[440px] w-full">
              {isLoading.value || isMyBit ? null : (
                <div className="lg:w-[440px] w-full md:mt-5 mt-4">
                  <h3 className="md:text-2xl md:font-semibold text-lg font-medium md:mb-5 mb-2">
                    Creator
                  </h3>
                  <AuthorInfo
                    authorId={bit?.author.id}
                    authorName={bit?.author.name}
                    authorImage={bit?.author.avatarUrl}
                    isPartner={bit?.author.isPartner}
                    authorUniqueLink={bit?.author.uniqueLink}
                    createdAt={bit?.createdAt}
                    updatedAt={bit?.updatedAt}
                  >
                    {/* {bit?.stripeAccountId && bit.isStripeConnected && (
                      <Donate
                        creatorId={bit?.author.id}
                        userId={profile?.id || 0}
                        stripeAccount={bit?.stripeAccountId || ''}
                        creatorName={bit?.author.name || ''}
                        creatorImage={bit?.author.avatarUrl || ''}
                        profileUniqueLink={bit?.author.uniqueLink || ''}
                        className="hidden md:block w-fit md:px-3 px-2 md:absolute md:right-0"
                        isAnonymous={isAnonymous}
                        isMyProfile={isMyBit}
                        paymentProvider={profile?.payment.provider}
                      />
                    )} */}
                  </AuthorInfo>
                  <hr className="lg:block hidden border-t border-gray-200 mt-7 w-full" />
                </div>
              )}
              {bit.project && (
                <LinkProject
                  bitId={bit.id}
                  isReadonly={true}
                  isMyProfile={isMyBit}
                  userUniqueLink={bit?.author.uniqueLink}
                  linkedProject={bit?.project || null}
                />
              )}
              {isMyBit ? (
                <div className="w-full flex gap-2 mt-5">
                  <Button
                    className="px-4 py-2 w-[49%]"
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      redirect(
                        `/${profile?.uniqueLink}/bits/published/${bit.id}`,
                      )
                    }
                  >
                    Edit bit
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild className="w-[49%]">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-error bg-transparent font-medium size-sm px-4 "
                      >
                        Delete bit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="gap-1">
                      <DialogHeader className="mb-4 ">
                        <DialogTitle></DialogTitle>
                        <div className="text-center font-semibold text-2xl px-16">
                          Are you sure you want to delete this Bit?
                        </div>
                        <DialogDescription className="text-center text-red-error">
                          This action cannot be undone
                        </DialogDescription>
                      </DialogHeader>
                      <hr className="border-t border-gray-200 pt-4" />
                      <DialogFooter className="flex md:justify-between">
                        <DialogClose asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-transparent font-medium size-sm px-4"
                          >
                            Cancel
                          </Button>
                        </DialogClose>
                        <Button
                          variant="destructive"
                          size="sm"
                          className="font-medium size-sm px-4"
                          onClick={handleDeleteBit}
                        >
                          {isLoading.value ? <Spinner /> : 'Delete this Bit'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
                !!readAlso?.length && (
                  <div className="flex flex-col">
                    <h2 className="text-2xl! font-semibold! my-5">Also read</h2>
                    <div className="flex flex-col gap-3 mb-5">
                      <BitsList bits={readAlso || []} />
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="md:w-[745px] w-full">
            {!isAnonymous && (
              <>
                <div className="flex w-full md:gap-3 gap-2 items-center mt-4 md:hidden">
                  {/* {bit?.stripeAccountId && bit.isStripeConnected && (
                    <Donate
                      creatorId={bit?.author.id}
                      userId={profile?.id || 0}
                      stripeAccount={bit?.stripeAccountId || ''}
                      creatorName={bit?.author.name || ''}
                      creatorImage={bit?.author.avatarUrl || ''}
                      profileUniqueLink={bit?.author.uniqueLink || ''}
                      className="hidden md:block w-fit md:px-3 px-2 md:absolute md:right-0"
                      isAnonymous={isAnonymous}
                      isMyProfile={isMyBit}
                    />
                  )} */}
                  <Button
                    size="sm"
                    variant="outline"
                    className={cn(
                      'text-black bg-transparent font-medium text-sm md:px-4 px-2 transition-all ease-in-out duration-200 gap-0.5',
                      {
                        'bg-black/80 text-white hover:bg-black hover:text-white':
                          like.isLiked,
                      },
                    )}
                    onClick={handleLikeBit}
                  >
                    <Icon
                      name={like.isLiked ? 'liked' : 'like'}
                      width={16}
                      height={16}
                      className="ml-1"
                    />
                    Like
                    <div className="h-4 border-r border-gray-300 md:mx-3 mx-1.5" />
                    {like.count}
                  </Button>
                  <ShareLink
                    slug={bit.slug}
                    type="bit"
                    className="md:px-7 px-2 md:h-8 hover:bg-black/50 hover:text-white transition-colors duration-150"
                  />
                  <Report bitId={bit?.id} type="bit" />
                </div>
                <hr className="md:hidden border-t border-gray-200 mt-7 w-full" />
              </>
            )}
          </div>
          <CommentsSection
            bitId={bit?.id}
            comments={comments}
            commentsCount={bit?.commentsCount}
            onCommentPosted={refreshComments}
            isAnonymous={isAnonymous}
          >
            <div className="md:flex hidden md:justify-end md:gap-2 md:absolute md:top-0 md:right-0">
              <Button
                size="sm"
                variant="outline"
                className={cn(
                  'text-black bg-transparent font-medium text-sm md:px-4 px-2 transition-all ease-in-out duration-200 gap-0.5',
                  {
                    'bg-black/80 text-white hover:bg-black hover:text-white':
                      like.isLiked,
                  },
                )}
                onClick={handleLikeBit}
              >
                <Icon
                  name={like.isLiked ? 'liked' : 'like'}
                  width={16}
                  height={16}
                  className="ml-1"
                />
                Like
                <div className="h-4 border-r border-gray-300 md:mx-3 mx-1.5" />
                {like.count}
              </Button>
              <ShareLink
                type="bit"
                slug={bit.slug}
                className="md:px-7 px-2 md:h-8 hover:bg-black/50 hover:text-white transition-colors duration-150"
              />
              <Report bitId={bit?.id} type="bit" />
            </div>
          </CommentsSection>
        </div>
      ) : (
        <PageLoader />
      )}
    </>
  );
}
